// My Route card for the existing Client Portal (P4) — client-safe rendering only.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { ArrowRight, Check, Circle, MapPin } from 'lucide-react';
import { CTA_ROUTES, MODE_EXPLAIN } from '../lib/gpsShared';

const API = process.env.REACT_APP_BACKEND_URL;

export const MyRouteCard = () => {
  const [journeys, setJourneys] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/auto-lifestyle/gps/my-route`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { journeys: [] })
      .then(d => setJourneys(d.journeys || []))
      .catch(() => setJourneys([]));
  }, []);

  if (journeys === null) return null;

  const j = journeys[0]; // backend already prioritizes ACTIVE → PLANNING → latest
  return (
    <Card className="p-5 sm:p-6 border-gray-200 mb-6" data-testid="my-route-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-red-600" />
          <h3 className="text-base font-bold text-black">My Route</h3>
        </div>
        <Link to="/auto-lifestyle" className="text-xs text-red-600 uppercase tracking-widest hover:text-black"
          data-testid="my-route-new-link">
          {j ? 'New Route' : 'Map My Route'}
        </Link>
      </div>
      {!j ? (
        <p className="text-sm text-gray-600" data-testid="my-route-empty">
          Where do you want your Auto Lifestyle to go? Map your route and ACE will chart the next stop.
        </p>
      ) : (
        <div data-testid="my-route-content">
          <div className="flex flex-wrap items-baseline gap-3 mb-1">
            <span className="text-2xl font-black" data-testid="my-route-mode">{j.route.route_mode}</span>
            <span className="text-xs text-gray-500">{MODE_EXPLAIN[j.route.route_mode]}</span>
          </div>
          <p className="text-sm text-gray-800 mb-3" data-testid="my-route-destination">
            Destination: {j.destination.text}
          </p>
          <ul className="space-y-1 mb-4">
            {j.route.route_reasons.slice(0, 2).map((r, i) => (
              <li key={i} className="text-xs text-gray-600 flex gap-2"><span className="text-red-600">♠</span>{r}</li>
            ))}
          </ul>
          <ol className="space-y-2 mb-4" data-testid="my-route-roadmap">
            {j.route.steps.map(s => (
              <li key={s.step_id} className="flex items-center gap-2.5 text-sm">
                {s.status === 'done' ? <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
                  : s.step_id === j.next_stop?.step_id ? <ArrowRight className="h-3.5 w-3.5 text-red-600 shrink-0" />
                    : <Circle className="h-2.5 w-2.5 text-gray-300 shrink-0" />}
                <span className={s.step_id === j.next_stop?.step_id ? 'font-semibold text-black' : 'text-gray-600'}>
                  {s.plain_language_action}
                </span>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap items-center gap-4">
            {CTA_ROUTES[j.next_stop?.step_type] ? (
              <Link to={CTA_ROUTES[j.next_stop.step_type].path} data-testid="my-route-next-stop-cta"
                className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-black transition-colors">
                Next Stop: {CTA_ROUTES[j.next_stop.step_type].label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : (
              <span className="text-xs text-gray-500">No action needed right now.</span>
            )}
            <Link to="/contact" className="text-xs uppercase tracking-widest text-gray-500 hover:text-black">Ask Your Auto Concierge</Link>
            {j.route.route_version > 1 && (
              <span className="text-[10px] uppercase tracking-widest text-gray-400" data-testid="my-route-version">
                Route updated · v{j.route.route_version}
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
